<script setup lang="ts">
import type { InputMode } from '@brighterly/lib-core-types/ui/inputs/types'

import { useDropzone } from '../../model/upload/dropzone.hooks'
import FileItem from './FileItem.vue'
import DropzoneBlock from './DropzoneBlock.vue'

defineOptions({
  name: 'DropzoneContainer',
})

const props = withDefaults(defineProps<DropzoneContainerProps>(), {
  title: 'drag & drop here',
  acceptedFilesType: 'PDF, DOC, DOCX, JPEG and PNG',
  dropzoneOptions: () => ({
    multiple: false,
  }),
  mode: 'normal',
  value: null,
})

const emit = defineEmits<{
  'click:drop': [File[] | File | null]
  'click:enter': [File[] | File | null]
  'click:leave': [File[] | File | null]
  'click:over': [File[] | File | null]
}>()

interface DropzoneOptions {
  multiple?: boolean
}

interface DropzoneContainerProps {
  title?: string
  acceptedFilesType?: string
  dropzoneOptions?: DropzoneOptions
  mode?: InputMode
  value?: File[] | File | null
}

const multipleRef = computed(() => props.dropzoneOptions.multiple || false)

const dropzoneBlockRef = useTemplateRef<InstanceType<typeof DropzoneBlock>>('dropzoneBlockRef')
const dropZoneRef = computed(() => dropzoneBlockRef.value?.dropZoneRef ?? null)
const initialFilesRef = computed(() => props.value)

const {
  files,
  isOverDropZone,
  removeFile,
  resetFiles,
  open,
} = useDropzone({
  dropZoneRef,
  multiple: multipleRef,
  initialFiles: initialFilesRef,
  onDrop: files => emit('click:drop', files),
  onEnter: files => emit('click:enter', files),
  onLeave: files => emit('click:leave', files),
  onOver: files => emit('click:over', files),
})

defineExpose({
  resetFiles,
})

</script>

<template>
  <div class="drop-block">
    <DropzoneBlock
      ref="dropzoneBlockRef"
      :title="title"
      :accepted-files-type="acceptedFilesType"
      :is-over-drop-zone="isOverDropZone"
      :mode="mode"
      @click:drop="open"
    />
    <div
      v-if="files.length > 0"
      class="drop-block__files"
    >
      <FileItem
        v-for="(item, index) in files"
        :key="`${item.file.name}-${index}`"
        :file="item.file"
        :error="item.error"
        @remove="removeFile(index)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss" src="./DropzoneContainer.scss" />
